import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { DEFAULT_AVATAR } from '../constants/sampleImages';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Sync with Firestore user profile
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
          // Create initial profile
          console.log("No user profile found, creating one for:", user.uid);
          const newProfile = {
            uid: user.uid,
            email: user.email || null,
            phone: user.phoneNumber || null,
            displayName: user.displayName || 'Guest User',
            photoURL: user.photoURL || DEFAULT_AVATAR,
            role: 'USER', // Default role
            followersCount: 0,
            followingCount: 0,
            lastActive: new Date().toISOString(),
            createdAt: new Date().toISOString(),
          };
          try {
            await setDoc(userDocRef, {
              ...newProfile,
              createdAt: serverTimestamp()
            });
            console.log("Successfully created user profile");
            setUserProfile(newProfile);
          } catch (err) {
            console.error("Failed to create user profile:", err);
            // Even if setDoc fails, set local profile to allow app to function
            setUserProfile(newProfile);
          }
        } else {
          const data = userDoc.data();
          console.log("Loaded existing user profile:", data.role);
          setUserProfile(data);
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Login Error:', error);
      if (error.code === 'auth/popup-blocked') {
        alert('The login popup was blocked. Please allow popups for this site or open the app in a new tab.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        // Silently handle cancellation or show a non-intrusive toast
      } else {
        alert('An error occurred during login: ' + error.message);
      }
    }
  };

  const loginWithPhone = async (phoneNumber, recaptchaId) => {
    const verifier = new RecaptchaVerifier(auth, recaptchaId, {
      size: 'invisible'
    });
    return await signInWithPhoneNumber(auth, phoneNumber, verifier);
  };

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ currentUser, userProfile, loading, login, loginWithPhone, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
